import React, { useEffect, useState } from 'react';
import { getData } from '@/services/api.service';
import { end_points } from '@/services/endpoints';
import Spinner from './spinner';
import { FiCopy } from 'react-icons/fi';
import { toast } from 'react-toastify';

type ReferralUser = {
  name?: string;
  email?: string;
};

type ReferralItem = {
  referred_user_id: number;
  reward_points: number;
  referred_user?: ReferralUser;
};

type ReferralData = {
  referral_code?: string;
  referral_link?: string;
  total_referrals?: number;
  successful_referrals?: number;
  reward_per_referral?: number;
  total_earned_points?: number;
  referral_list?: ReferralItem[];
};

const MyReferrals = () => {
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState<ReferralData>({});

  const getMyReferrals = async () => {
    setLoading(true);
    try {
      const response = await getData(end_points.myReferrals.url);
      setReferrals(response?.data?.data || response?.data || {});
    } catch (error) {
      console.log('Failed to fetch referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMyReferrals();
  }, []);

  const handleCopyReferralLink = async () => {
    const referralLink = referrals?.referral_link;
    const referralCode = referrals?.referral_code;

    let currentBaseReferralLink = '';
    const baseUrl = `${window.location.origin}/service/customer`;

    if (referralLink) {
      try {
        const parsedUrl = new URL(referralLink, window.location.origin);
        currentBaseReferralLink = `${baseUrl}${parsedUrl.pathname}${parsedUrl.search}`;
      } catch {
        currentBaseReferralLink = `${baseUrl}${referralLink}`;
      }
    } else if (referralCode) {
      currentBaseReferralLink =
        `${baseUrl}/register?ref=${referralCode}`;
    }

    if (!currentBaseReferralLink) {
      toast.error('Referral link not available');
      return;
    }

    try {
      await navigator.clipboard.writeText(currentBaseReferralLink);
      toast.success('Referral link copied');
    } catch (error) {
      console.log('Copy failed:', error);
      toast.error('Failed to copy referral link');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  const referralList = referrals?.referral_list || [];

  return (
    <div className="col-md-9 tabs style1">
      <div className="row">
        <div className="col-md-12">
          <div className="user-dashboard-tab__content tab-content">
            <div className="tab-pane fade show active" id="my_referrals">
              <div className="card-box">
                <h5 className="mb-3">My Referrals</h5>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <div className="mb-2 d-flex align-items-center">
                      <strong className="me-2">Referral Code:</strong>
                      <span>{referrals?.referral_code || '-'}</span>
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2"
                        onClick={handleCopyReferralLink}
                        title="Copy referral link"
                      >
                        <FiCopy size={16} />
                      </button>
                    </div>
                    <div className="mb-2">
                      <strong>Total Referrals:</strong> {referrals?.total_referrals ?? 0}
                    </div>
                    <div className="mb-2">
                      <strong>Total Earned Points:</strong>{' '}
                      {referrals?.total_earned_points ?? 0}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Successful Referrals:</strong>{' '}
                      {referrals?.successful_referrals ?? 0}
                    </div>
                    <div className="mb-2">
                      <strong>Reward Per Referral:</strong>{' '}
                      {referrals?.reward_per_referral ?? 0}
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Reward Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralList.length > 0 ? (
                        referralList.map((item) => (
                          <tr key={item.referred_user_id}>
                            <td>{item?.referred_user?.name || '-'}</td>
                            <td>{item?.referred_user?.email || '-'}</td>
                            <td>{item?.reward_points || '0'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center">
                            No referrals found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyReferrals;
